import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Header from "../Header/Header";
import { Button, CircularProgress, Grid, Box } from "@mui/material";
import batchService from "../../services/BatchService";
import { SiteDTO } from "@beable/roster-backend-client-v1";
import EmployeeSiteSelector from "./EmployeeSiteSelector";
import SchoolSelector from "../Common/Selectors/SchoolSelector";
import BaseDialog from "../Common/BaseDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import { SubHeader } from "../Common/Layout/SubHeader";
import { getStyles } from "./styles";

const TextContainer = styled(Grid)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  width: '100%',
}));

const StandardSiteSelector = styled(EmployeeSiteSelector)({
  width: '100%',
});

const EBSchoolSelector = styled(SchoolSelector)({
  width: '100%',
});

export default function RemoveSchool() {
  const navigate = useNavigate();

  const [selectedSite, setSelectedSite] = useState<SiteDTO | undefined>(
    undefined
  );
  const [schoolId, setSchoolId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const theme = useTheme();
  const styles = getStyles(theme);

  const handleSave = async () => {
    setSubmitting(true);
    const results = await batchService.removeEmployeeBatch(
      selectedSite!,
      schoolId
    );
    if (results) {
      setDialogOpen(true);
    } else {
      alert(`Error removing Beable user account`);
    }
    setSubmitting(false);
  };

  const goToBatchStatus = () => {
    setDialogOpen(false);
    navigate(
      `/batch-status`
    );
  };

  return (
    <>
      <Header />
      <SubHeader>
        <Typography variant="h6" component="span">
          Remove Beable Accounts
        </Typography>
      </SubHeader>
      <Box sx={styles.root}>
        <Grid container sx={{ width: '70%', mx: 'auto' }}>
          <Grid container size={12} sx={{
            justifyContent: "center"
          }}>
            <Grid size={8}>
              <Grid
                size={12}
                sx={{
                  justifyContent: "flex-start",
                  marginTop: '32px'
                }}>
                  <StandardSiteSelector onSelect={setSelectedSite} />
              </Grid>
              <Grid
                size={12}
                sx={{
                  justifyContent: "flex-start",
                  marginLeft: 0,
                  marginTop: '32px'
                }}>
                  <EBSchoolSelector
                    siteId={selectedSite ? selectedSite.id : 0}
                    schoolId={schoolId}
                    onChange={setSchoolId}
                    allOption={false}
                    disabled={!selectedSite}
                    variant="outlined"
                  />
              </Grid>
              <TextContainer container size={12} sx={{ justifyContent: 'center' }}>
                {submitting && (
                  <Box sx={styles.loaderContainer}>
                    <CircularProgress disableShrink />
                  </Box>
                )}
                {!submitting && (
                  <Button
                    variant="contained"
                    color="primary"
                    aria-label="save"
                    onClick={handleSave}
                    disabled={!selectedSite || !schoolId}
                  >
                    Remove
                  </Button>
                )}
              </TextContainer>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <BaseDialog
        open={dialogOpen}
        header="Remove account"
        showCloseIcon={true}
        showCloseButton={false}
        onClose={() => setDialogOpen(false)}
      >
        <Box
          sx={{
            minHeight: 150,
            display: "flex",
            alignItems: "center"
          }}>
          <Box sx={{
            width: "100%"
          }}>
            <DialogContentText>
              New roster batch started. Click 'Batch Status' to monitor
              progress. When available, click 'Resume' to complete the process.
              <br />
              **Please wait a few minutes and then refresh the page to view the
              batch.
            </DialogContentText>
            <DialogActions>
              <Button
                onClick={goToBatchStatus}
                variant="contained"
                color="primary"
              >
                Batch Status
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </BaseDialog>
    </>
  );
}
